import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { Card, CardContent } from './ui/card';
import { Star, Users } from 'lucide-react';

export default function SimilarArtists({ artistId }) {
  const navigate = useNavigate();
  const [similarArtists, setSimilarArtists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (artistId) {
      fetchSimilarArtists();
    }
  }, [artistId]);

  const fetchSimilarArtists = async () => {
    try {
      const response = await apiClient.get(`/artists/${artistId}/similar`);
      setSimilarArtists(response.data);
    } catch (error) {
      // Silent fail
    } finally {
      setLoading(false);
    }
  };

  if (loading || similarArtists.length === 0) {
    return null;
  }

  return (
    <div className="mt-8">
      <h2 className="text-xl font-bold flex items-center gap-2 mb-4">
        <Users className="text-violet-400" size={20} />
        Similar Artists
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {similarArtists.map((artist) => (
          <Card
            key={artist.artist_id}
            className="glass-default border-white/10 cursor-pointer hover-lift overflow-hidden"
            onClick={() => navigate(`/artist/${artist.artist_id}`)}
          >
            <div className="aspect-square relative bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20">
              {artist.media_urls?.[0] ? (
                <img
                  src={artist.media_urls[0]}
                  alt={artist.stage_name || artist.name || 'Artist'}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => { e.target.style.display = 'none'; const f = e.target.nextSibling; if (f) { f.classList.remove('hidden'); f.classList.add('flex'); } }}
                />
              ) : null}
              <div className={`w-full h-full items-center justify-center ${artist.media_urls?.[0] ? 'hidden' : 'flex'}`}>
                <Star className="w-8 h-8 text-violet-400/50" />
              </div>
              {artist.rating > 0 && (
                <div className="absolute top-2 right-2 glass-heavy rounded-full px-2 py-0.5 flex items-center gap-1">
                  <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                  <span className="text-xs font-semibold">{artist.rating.toFixed(1)}</span>
                </div>
              )}
            </div>
            <CardContent className="p-3">
              <p className="font-semibold truncate text-sm">
                {artist.stage_name || artist.name || artist.bio?.split(' ').slice(0, 3).join(' ') || 'Artist'}
              </p>
              <div className="flex flex-wrap gap-1 mt-1">
                {artist.genres?.slice(0, 2).map((genre, idx) => (
                  <span key={idx} className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-500/20 text-violet-300">
                    {genre}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
